import shaDigest from 'sha.js'

import {apiRequest, del, get, post} from '../../utils/apiRequest';

type AssetData = Editor.AssetData
type Translatable = Editor.Translatable
type TranslatableMap = Editor.TranslatableMap

const apiPath = '/api/v1/assets/asset'

type AssetDataAPIData = {
  checksum: string
  meta: object
  modified: string
  size: number
  type: string
  locale?: string
}

type AssetAPIData = {
  id: string
  name: string
  locale?: string
  assetData: AssetDataAPIData[]
}

type Loopback3APIResponseData = {
  data: AssetAPIData[]
}

type Loopback3APICreateResponseData = {
  id: string
}

type Loopback3APIUploadResponseData = {
  data: AssetAPIData[][]
}

export const getBaseUrl = (id: string, locale: string) => `${apiPath}/${id}/${locale}`

export const getDownloadUrl = async (baseURL: string, hashInput: string) => {
  // calculate SHA-1 of input string
  let hash

  /*
    * From DOM documentation:
    *
    * SubtleCrypto
    *    Secure context
    *    This feature is available only in secure contexts (HTTPS),
    *    in some or all supporting browsers.
    */
  if (crypto.subtle) {
    const encoder = new TextEncoder()
    const data = encoder.encode(hashInput)
    const hashBuffer = await crypto.subtle.digest('SHA-1', data)

    // convert digest to hex string
    const octets = new Uint8Array(hashBuffer)
    hash = Array
      .from(octets)
      .map(octet => octet.toString(16).padStart(2, '0'))
      .join('')

  } else {
    hash = shaDigest('sha1').update(hashInput).digest('hex')
  }

  // add hash as cache buster - otherwise browser may show obsolete asset content
  return `${baseURL}/download?hash=${hash}`
}

export const cloneAO1 = async (source: Translatable, locale: string): Promise<AssetData> => {
  const {id, locale: sourceLocale} = source
  const baseURL = `${apiPath}/${id}`

  // Asset + AssetData for locale
  const {
    data: [{
      name: filename,
      assetData: [{
        checksum,
        modified,
        size,
        type,
      }]
    }],
  } = await get(
    `${baseURL}/${sourceLocale}/${locale}/clone`
  ) as Loopback3APIResponseData
  const url = await getDownloadUrl(`${baseURL}/${locale}`, `${type}${size}${checksum}${modified}`)

  return {
    checksum,
    filename,
    id,
    locale,
    type,
    url,
  }
}

export const fetchAO1s = async (aoIds: string[], locale: string): Promise<TranslatableMap> => {
  if (aoIds.length === 0) return {}

  // Loopback3 query filter
  const query = {
    where: {
      id: {
        inq: aoIds,
      }
    },
    fields: { // fields from Asset instance to include in response
      id: true,
      name: true,
      // include asset master locale for completeness (not required on translated side)
      locale: true,
    },
    include: {
      relation: 'assetData',
      scope: {
        where: {
          locale,
        },
        fields: { // fields from AssetData instance to include in response
          checksum: true,
          modified: true,
          size: true,
          type: true,
          locale: true,
        },
      },
    },
  }
  const aos = await get(
    `${apiPath}?filter=${JSON.stringify(query)}`,
  ) as AssetAPIData[]

  // return targetId->Translatable map; do NOT fallback — only use exact target-locale AssetData
  return await aos.reduce(
    async (
      promise,
      ao
    ) => {
      const { id, name: filename } = ao
      // use target-locale AssetData if present
      const primary = ao.assetData?.[0]
      let checksum: string | undefined
      let modified: string | undefined
      let size: number | undefined
      let type: string | undefined
      let url = ''
      if (primary) {
        ({ checksum, modified, size, type } = primary)
        const baseURL = getBaseUrl(id, locale)
        url = await getDownloadUrl(baseURL, `${type}${size}${checksum}${modified}`)
      } else {
        // no data at all for target locale; skip including a preview and let UI show upload placeholder
        return promise
      }

      const translatable: AssetData = {
        checksum: checksum!,
        filename,
        id,
        locale, // keep target locale for editing/upload flow
        type: type!,
        url,
      }

      const map = await promise
      map[id] = translatable
      return map
    },
    Promise.resolve({} as TranslatableMap)
  )
}

export const fetchAO1Sources = async (aoIds: string[]): Promise<TranslatableMap> => {
  if (aoIds.length === 0) return {}

  // Fetch assets with their master locale and all available AssetData entries
  const query = {
    where: { id: { inq: aoIds } },
    fields: { id: true, name: true, locale: true },
    include: {
      relation: 'assetData',
      scope: {
        fields: { checksum: true, modified: true, size: true, type: true, locale: true },
      },
    },
  }
  const assets = await get(
    `${apiPath}?filter=${JSON.stringify(query)}`,
  ) as AssetAPIData[]

  return await assets.reduce(
    async (promise, asset) => {
      const { id, name: filename, locale: masterLocale } = asset
      // pick the assetData that matches the asset's master locale
      let primary = asset.assetData?.find(ad => ad.locale === masterLocale)
      // if not found, be resilient and use the first available entry
      if (!primary && asset.assetData?.length) primary = asset.assetData[0]
      if (!primary) return promise

      const { checksum, modified, size, type } = primary
      const baseURL = getBaseUrl(id, masterLocale || primary.locale!)
      const url = await getDownloadUrl(baseURL, `${type}${size}${checksum}${modified}`)

      const translatable: AssetData = {
        checksum: checksum!,
        filename,
        id,
        locale: masterLocale || primary.locale!,
        type: type!,
        url,
      }

      const map = await promise
      map[id] = translatable
      return map
    },
    Promise.resolve({} as TranslatableMap)
  )
}

const createAsset = async (name: string, locale: string, translationFolderId?: string) => {
  if (!translationFolderId)
    throw new Error('Trying to create new asset without translation folder')

    const now = new Date()
  const {id} = await post(
    apiPath,
    {
      created: now,
      folder: translationFolderId,
      locale,
      modified: now,
      name,
    }
  ) as Loopback3APICreateResponseData

  return id;
}

export const saveAO1 = async (
  translatable: Translatable,
  file: File,
  translationFolderId?: string
): Promise<AssetData> => {
  const {checksum, locale} = translatable
  // create new asset if no asset ID is specified
  const id = translatable.id || await createAsset(file.name, locale, translationFolderId)
  const baseURL = getBaseUrl(id, locale)

  // asset-api expects file data behind Form entry file=
  const formData = new FormData();
  formData.append('file', file);

  // Asset + AssetData for locale
  const {
    data: [
      [{
        name: filename,
        assetData: [{
          modified,
          size,
          type,
        }]
      }],
    ]
  } = await apiRequest(
    `${baseURL}/${checksum}/upload`,
    'POST', {
      body: formData,
    }
  ) as Loopback3APIUploadResponseData
  const url = await getDownloadUrl(baseURL, `${type}${size}${checksum}${modified}`)

  return {
    ...translatable,
    filename,
    id,
    type,
    url,
  }
}

export const deleteAO1 = async (
  translatable: Translatable,
): Promise<void> => {
  const {id, locale} = translatable
  const baseURL = getBaseUrl(id, locale)
  await del(baseURL)
}
